// Compare/summary table button and modal functionality

import { getAuthHeaders } from '../api/auth.js';
import { ensurePdfLibsLoaded } from './pdf-loader.js';

/** Add data-label to table cells for responsive card layout on small screens */
function addTableDataLabels(container) {
    container.querySelectorAll('table').forEach((table) => {
        let headers = Array.from(table.querySelectorAll('thead th')).map((th) => th.textContent.trim());
        const tbodyRows = Array.from(table.querySelectorAll('tbody tr'));
        let headerRowIndex = -1;
        if (headers.length === 0 && tbodyRows.length > 0) {
            const firstRow = tbodyRows[0];
            const firstRowCells = firstRow.querySelectorAll('th, td');
            headers = Array.from(firstRowCells).map((cell) => cell.textContent.trim());
            if (headers.length > 0) {
                headerRowIndex = 0;
                firstRow.classList.add('summary-table-header-row');
            }
        }
        tbodyRows.forEach((tr, rowIndex) => {
            if (rowIndex === headerRowIndex) return;
            const cells = tr.querySelectorAll('td');
            if (headers.length === 0) return;
            cells.forEach((td, i) => {
                if (headers[i]) td.setAttribute('data-label', headers[i]);
            });
        });
    });
}
import { formatMessage } from './messages.js';
import { getCurrentResearchId } from '../state/research.js';
import { htmlToPlainText } from './html.js';

export function createCompareButton(timestamp, searchId) {
    const compareButton = document.getElementById(`comparebtn-${timestamp}`);
    
    if (!compareButton) {
        console.error(`Compare button not found: comparebtn-${timestamp}`);
        return;
    }
    
    console.log(`Setting up compare button for timestamp ${timestamp} with searchId ${searchId}`);
    
    // Create modal overlay
    const modalOverlay = document.createElement('div');
    modalOverlay.id = `modal-overlay-${timestamp}`;
    modalOverlay.className = 'modal-overlay hidden';
    
    // Create modal content
    const modalContent = document.createElement('div');
    modalContent.id = `modal-content-${timestamp}`;
    modalContent.className = 'modal-content';
    
    // Create modal header (close button only)
    const modalHeader = document.createElement('div');
    modalHeader.className = 'modal-header';
    modalHeader.innerHTML = `
        <h3>Summary Table</h3>
        <div style="display: flex; gap: 8px; align-items: center;">
            <button class="modal-close-btn" id="modal-close-${timestamp}" title="Close">
                <span class="material-symbols-rounded notranslate">close</span>
            </button>
        </div>
    `;
    
    // Create modal body (scrollable content area)
    const modalBody = document.createElement('div');
    modalBody.id = `modal-body-${timestamp}`;
    modalBody.className = 'modal-body summary-table-modal-body';
    
    // Content wrapper - table and text get inserted here
    const modalBodyContent = document.createElement('div');
    modalBodyContent.className = 'modal-body-content';
    
    // Modal toolbar (download button) - inside modal body, right below content, above scrollbar
    const modalToolbar = document.createElement('div');
    modalToolbar.id = `modal-toolbar-${timestamp}`;
    modalToolbar.className = 'modal-toolbar hidden';
    modalToolbar.innerHTML = `
        <button class="icon-btn" id="modal-copy-${timestamp}" data-tooltip="Copy the response including the Summary Table" data-short-label="Copy Full Response">
            <span class="material-symbols-rounded notranslate">content_copy</span>
        </button>
        <button class="icon-btn" id="modal-download-${timestamp}" data-tooltip="Export the response including the Summary Table as a PDF" data-short-label="Download Full PDF">
            <span class="material-symbols-rounded notranslate">download</span>
        </button>
    `;
    
    modalBody.appendChild(modalBodyContent);
    modalBody.appendChild(modalToolbar);
    modalContent.appendChild(modalHeader);
    modalContent.appendChild(modalBody);
    modalOverlay.appendChild(modalContent);
    
    // Append modal to body
    document.body.appendChild(modalOverlay);
    
    // Get references to copy, download buttons and modal toolbar
    const copyBtn = document.getElementById(`modal-copy-${timestamp}`);
    const downloadBtn = document.getElementById(`modal-download-${timestamp}`);
    const modalToolbarEl = document.getElementById(`modal-toolbar-${timestamp}`);
    
    let comparisonData = null; // Cache for comparison data (formatted HTML)
    let rawComparisonData = null; // Cache for raw comparison data (markdown)
    let isFetching = false; // Flag to prevent multiple simultaneous fetches
    
    // Function to open modal
    const openModal = async () => {
        modalOverlay.classList.remove('hidden');
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
        
        // If we already have the comparison data, use it
        if (comparisonData) {
            modalBodyContent.innerHTML = comparisonData;
            addTableDataLabels(modalBodyContent);
            // Show modal toolbar (download button) if we have comparison data
            if (modalToolbarEl && rawComparisonData) {
                modalToolbarEl.classList.remove('hidden');
                modalToolbarEl.classList.add('visible');
            }
            return;
        }
        
        // Hide modal toolbar while loading
        if (modalToolbarEl) {
            modalToolbarEl.classList.add('hidden');
            modalToolbarEl.classList.remove('visible');
        }
        
        // Show loading state only if we don't have cached data
        modalBodyContent.innerHTML = '<div class="compare-loading"><div class="spinner"></div><span>Generating Summary Table...</span></div>';
        
        // Prevent multiple simultaneous fetches
        if (isFetching) {
            return;
        }
        isFetching = true;
        
        try {
            const currentResearchId = getCurrentResearchId();
            let response;
            if (currentResearchId) {
                // Pro user with research session — look up from DynamoDB
                const compareUrl = `/compare-studies?searchId=${encodeURIComponent(searchId)}&research_id=${encodeURIComponent(currentResearchId)}`;
                response = await fetch(compareUrl, { headers: getAuthHeaders() });
            } else {
                // No research session — post citations directly
                const compareButton = document.getElementById(`comparebtn-${timestamp}`);
                const citationsB64 = compareButton ? compareButton.getAttribute('data-citations-b64') : null;
                const citations = citationsB64 ? JSON.parse(atob(citationsB64)) : [];
                response = await fetch('/compare-studies', {
                    method: 'POST',
                    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ citations }),
                });
            }
            const data = await response.json();
            
            if (data.comparison) {
                // Store raw comparison data for PDF generation
                rawComparisonData = data.comparison;
                // Format and display the comparison
                const formattedComparison = formatMessage(data.comparison);
                comparisonData = formattedComparison; // Cache the result
                modalBodyContent.innerHTML = formattedComparison;
                addTableDataLabels(modalBodyContent);
                // Show modal toolbar since we have comparison data
                if (modalToolbarEl) {
                    modalToolbarEl.classList.remove('hidden');
                    modalToolbarEl.classList.add('visible');
                }
            } else if (data.error) {
                // Display professional error message without "Error:" prefix
                modalBodyContent.innerHTML = `<p class="compare-error">${DOMPurify.sanitize(data.error)}</p>`;
                // Keep modal toolbar hidden if there's an error
                if (modalToolbarEl) {
                    modalToolbarEl.classList.add('hidden');
                    modalToolbarEl.classList.remove('visible');
                }
            }
        } catch (error) {
            modalBodyContent.innerHTML = `<p class="compare-error">Error loading comparison: ${DOMPurify.sanitize(error.message)}</p>`;
            // Keep modal toolbar hidden if there's an error
            if (modalToolbarEl) {
                modalToolbarEl.classList.add('hidden');
                modalToolbarEl.classList.remove('visible');
            }
        } finally {
            isFetching = false;
        }
    };
    
    // Function to download PDF with summary table
    const downloadSummaryPDF = async () => {
        if (!rawComparisonData) {
            console.error('[Research] No comparison data available for PDF download');
            return;
        }

        // Get the full response content (same as regular download)
        const el = document.getElementById(`output-${timestamp}`);
        if (!el) {
            console.error('[Research] Output element not found for PDF download');
            return;
        }

        await ensurePdfLibsLoaded();
        
        // Process the response HTML to ensure consistent spacing
        const responseDiv = document.createElement('div');
        responseDiv.innerHTML = el.innerHTML;
        
        // Ensure consistent spacing between headings and paragraphs in response
        const responseHeadings = responseDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
        responseHeadings.forEach(heading => {
            heading.style.marginTop = '0';
            heading.style.marginBottom = '10px';
        });
        
        const responseParagraphs = responseDiv.querySelectorAll('p');
        responseParagraphs.forEach(p => {
            // Check if paragraph follows a heading
            const prevSibling = p.previousElementSibling;
            if (prevSibling && (prevSibling.tagName === 'H1' || prevSibling.tagName === 'H2' || prevSibling.tagName === 'H3' || prevSibling.tagName === 'H4' || prevSibling.tagName === 'H5' || prevSibling.tagName === 'H6')) {
                p.style.marginTop = '0';
            } else {
                p.style.marginTop = '0';
            }
            
            // Check if paragraph is followed by a heading - maintain spacing
            const nextSibling = p.nextElementSibling;
            if (nextSibling && (nextSibling.tagName === 'H1' || nextSibling.tagName === 'H2' || nextSibling.tagName === 'H3' || nextSibling.tagName === 'H4' || nextSibling.tagName === 'H5' || nextSibling.tagName === 'H6')) {
                p.style.marginBottom = '15px'; // Maintain spacing before heading
            } else {
                // Check if this is the last paragraph (will be followed by Summary Table)
                const isLastParagraph = !p.nextElementSibling || (p.nextElementSibling && p.nextElementSibling.tagName !== 'P');
                if (isLastParagraph) {
                    p.style.marginBottom = '0'; // No margin before Summary Table section
                } else {
                    p.style.marginBottom = '0';
                }
            }
        });
        
        const processedResponseHTML = responseDiv.innerHTML;
        
        // Get References section content if available
        const referencesSection = document.getElementById(`references-${timestamp}`);
        let referencesHTML = '';
        if (referencesSection && !referencesSection.classList.contains('hidden')) {
            // Clone the references section to avoid modifying the original
            const clonedSection = referencesSection.cloneNode(true);
            // Strip the "Summarize" button — pdfmake renders its icon ligature/label as plain text
            clonedSection.querySelectorAll('.abstract-insight-btn').forEach((btn) => btn.remove());
            // Add margin-top to ordered list for spacing after heading
            const ol = clonedSection.querySelector('ol');
            if (ol) {
                ol.style.marginTop = '12px';
            }
            // Add margin-bottom to list items for spacing between citations
            const listItems = clonedSection.querySelectorAll('li');
            listItems.forEach(item => {
                item.style.marginBottom = '12px';
            });
            referencesHTML = clonedSection.innerHTML;
        }
        
        // Format the summary table (convert markdown to HTML)
        let summaryTableHTML = formatMessage(rawComparisonData);
        
        // Process the table HTML to add inline styles for PDF compatibility
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = summaryTableHTML;
        
        // Find all tables and paragraphs
        const tables = tempDiv.querySelectorAll('table');
        const allParagraphs = tempDiv.querySelectorAll('p');
        
        // Get all child elements in document order
        const walker = document.createTreeWalker(
            tempDiv,
            NodeFilter.SHOW_ELEMENT,
            null,
            false
        );
        const elements = [];
        let node;
        while (node = walker.nextNode()) {
            elements.push(node);
        }
        
        // Remove margins from paragraphs that appear before any table
        allParagraphs.forEach(p => {
            const pIndex = elements.indexOf(p);
            let isBeforeTable = false;
            
            // Check if any table comes after this paragraph
            tables.forEach(table => {
                const tableIndex = elements.indexOf(table);
                if (tableIndex !== -1 && tableIndex > pIndex) {
                    isBeforeTable = true;
                }
            });
            
            if (isBeforeTable) {
                // Remove margins from paragraphs before table
                p.style.marginTop = '0';
                p.style.marginBottom = '0';
            } else {
                // For paragraphs after table, ensure consistent spacing
                // Check if paragraph follows a heading
                const prevSibling = p.previousElementSibling;
                if (prevSibling && (prevSibling.tagName === 'H1' || prevSibling.tagName === 'H2' || prevSibling.tagName === 'H3' || prevSibling.tagName === 'H4' || prevSibling.tagName === 'H5' || prevSibling.tagName === 'H6')) {
                    p.style.marginTop = '0';
                } else {
                    p.style.marginTop = '0';
                }
                
                // Check if paragraph is followed by a heading - maintain spacing
                const nextSibling = p.nextElementSibling;
                if (nextSibling && (nextSibling.tagName === 'H1' || nextSibling.tagName === 'H2' || nextSibling.tagName === 'H3' || nextSibling.tagName === 'H4' || nextSibling.tagName === 'H5' || nextSibling.tagName === 'H6')) {
                    p.style.marginBottom = '15px'; // Maintain spacing before heading
                } else {
                    p.style.marginBottom = '0';
                }
            }
        });
        
        // Apply styles directly to table elements
        tables.forEach(table => {
            table.style.width = 'calc(100% - 20px)';
            table.style.maxWidth = 'calc(100% - 20px)';
            table.style.marginRight = '20px';
            table.style.tableLayout = 'fixed';
            table.style.wordWrap = 'break-word';
            table.style.overflowWrap = 'break-word';
            table.style.borderCollapse = 'collapse';
            table.style.marginBottom = '0';
            table.style.marginTop = '0';
            
            // Style all cells with proper constraints
            const cells = table.querySelectorAll('td, th');
            cells.forEach(cell => {
                cell.style.padding = '6px';
                cell.style.wordWrap = 'break-word';
                cell.style.overflowWrap = 'break-word';
                cell.style.fontSize = '10px';
                cell.style.lineHeight = '1.3';
                cell.style.verticalAlign = 'top';
            });
        });
        
        // Ensure consistent spacing for headings
        const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
        headings.forEach(heading => {
            heading.style.marginTop = '0';
            heading.style.marginBottom = '10px';
        });
        
        summaryTableHTML = tempDiv.innerHTML;
        
        // Create the summary table section with minimal spacing above
        const summaryTableSection = `
            <div style="margin-bottom: 0; margin-top: -10px;">
                <h2 style="font-size: 1.5rem; margin-bottom: 10px; margin-top: 0; color: #333;">Summary Table</h2>
                <div style="margin-right: 20px; margin-top: 0; margin-bottom: 0;">
                    ${summaryTableHTML}
                </div>
            </div>
        `;
        
        const disclaimerHTML = `
<div style="
  font-size: 0.75em; 
  font-style: italic; 
  color: #555; 
  text-align: center; 
  margin-top: 40px;
  line-height: 1.4;
">
  <p style="margin: 0;">
    Response generated using 
    <a href="https://www.bio-med-search.ai" target="_blank">https://www.bio-med-search.ai</a><br>
    This document summarizes findings from PubMed-indexed literature and is intended for informational and research purposes only. It does not constitute medical advice.
  </p>
</div>
`;

        // Combine: Full Response + Summary Table + References + Disclaimer
        const html = processedResponseHTML + summaryTableSection + (referencesHTML ? referencesHTML : '') + disclaimerHTML;

        // Convert HTML -> pdfmake document structure
        // Note: htmlToPdfmake is a global variable from CDN
        const content = htmlToPdfmake(html, { window: window });

        const docDefinition = {
            content: content,
            defaultStyle: {
                // fallback styles — pdfMake has different styling model
                fontSize: 11
            }
        };

        pdfMake.createPdf(docDefinition).download('bio-med-search-response-with-summary-table.pdf');

        fetch(`/log_response?button_click=${encodeURIComponent("Download Summary Table PDF")}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
    };

    // Function to copy full response (same content as Download Full Response)
    const copyFullResponse = () => {
        if (!rawComparisonData) {
            console.error('[Research] No comparison data available for copy');
            return;
        }
        const el = document.getElementById(`output-${timestamp}`);
        if (!el) {
            console.error('[Research] Output element not found for copy');
            return;
        }
        let responseText = htmlToPlainText(el);
        const responseWithoutReferences = responseText.replace(/\n\nReferences[\s\S]*$/i, '').trim();
        let summaryTableText = '';
        const summaryTableHTML = formatMessage(rawComparisonData);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = summaryTableHTML;
        summaryTableText = htmlToPlainText(tempDiv);
        summaryTableText = '\n\nSummary Table\n\n' + summaryTableText.trim();
        let referencesText = '';
        const referencesSection = document.getElementById(`references-${timestamp}`);
        if (referencesSection && !referencesSection.classList.contains('hidden')) {
            const clonedReferences = referencesSection.cloneNode(true);
            clonedReferences.querySelectorAll('.abstract-insight-btn').forEach((btn) => btn.remove());
            const listItems = clonedReferences.querySelectorAll('li');
            if (listItems.length > 0) {
                referencesText = '\n\nReferences\n\n';
                listItems.forEach((item, index) => {
                    referencesText += `${index + 1}. ${item.textContent.trim()}`;
                    if (index < listItems.length - 1) referencesText += '\n\n';
                });
            } else {
                referencesText = '\n\n' + clonedReferences.textContent;
            }
        }
        const finalText = responseWithoutReferences + summaryTableText + referencesText +
            '\n\nGenerated using Bio-Med-Search (https://www.bio-med-search.ai)\n\n' +
            'This document summarizes findings from PubMed-indexed literature and is intended for informational and research purposes only. It does not constitute medical advice.';
        navigator.clipboard.writeText(finalText);
        if (copyBtn) {
            copyBtn.innerHTML = '<span class="material-symbols-rounded notranslate">check</span>';
            setTimeout(() => {
                copyBtn.innerHTML = '<span class="material-symbols-rounded notranslate">content_copy</span>';
            }, 2000);
        }
        fetch(`/log_response?button_click=${encodeURIComponent("Copy Full Response")}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
    };

    // Function to close modal
    const closeModal = () => {
        modalOverlay.classList.add('hidden');
        document.body.style.overflow = ''; // Restore scrolling
    };
    
    // Button click handler
    compareButton.addEventListener('click', openModal);
    
    // Copy button handler
    if (copyBtn) {
        copyBtn.addEventListener('click', copyFullResponse);
    }
    // Download button handler
    if (downloadBtn) {
        downloadBtn.addEventListener('click', downloadSummaryPDF);
    }
    
    // Close button handler
    const closeBtn = document.getElementById(`modal-close-${timestamp}`);
    closeBtn.addEventListener('click', closeModal);
    
    // Close on overlay click (outside modal content)
    modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
            closeModal();
        }
    });
    
    // Close on Escape key
    document.addEventListener('keydown', function escapeHandler(e) {
        if (e.key === 'Escape' && !modalOverlay.classList.contains('hidden')) {
            closeModal();
        }
    });
    
    console.log(`Compare button setup complete for timestamp ${timestamp}`);
}
